import { useState, useEffect, useCallback } from 'react';
import { ProtectedNewsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Markdown from './Markdown';

/* ── helpers ─────────────────────────────────────────────────────────── */

const fmtDate = (s) => {
  if (!s) return '';
  try {
    const d = new Date(s);
    if (isNaN(d)) return s;
    const m = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
    return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return s; }
};

const TAG_COLORS = {
  'Stämma':       { bg: 'rgba(44,74,46,.1)',   color: 'var(--forest)' },
  'Fornbobladet': { bg: 'rgba(61,107,122,.1)', color: 'var(--lake)' },
  'Städdag':      { bg: 'rgba(176,140,48,.1)', color: '#9a7a00' },
  'Valberedning': { bg: 'rgba(130,70,130,.1)', color: '#7a3d7a' },
};

function TagBadge({ tagg }) {
  const cfg = TAG_COLORS[tagg] || { bg: 'rgba(100,100,100,.1)', color: 'var(--text-soft)' };
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
      padding: '0.2rem 0.55rem', borderRadius: 3,
      background: cfg.bg, color: cfg.color, whiteSpace: 'nowrap'
    }}>
      {tagg}
    </span>
  );
}

/* ── toast ───────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem',
      fontSize: '0.88rem'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

/* ── news drawer ─────────────────────────────────────────────────────── */

const EMPTY = { titel: '', text: '', datum: '', tagg: '', pinned: false };

function NewsDrawer({ open, onClose, onSave, item }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(item ? {
      titel: item.titel || '',
      text: item.text || '',
      datum: item.datum || new Date().toISOString().split('T')[0],
      tagg: item.tagg || '',
      pinned: item.pinned || false
    } : { ...EMPTY, datum: new Date().toISOString().split('T')[0] });
    setError('');
  }, [open, item]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.titel.trim()) { setError('Rubrik är obligatorisk'); return; }
    if (!form.text.trim()) { setError('Text är obligatorisk'); return; }
    setSaving(true);
    try {
      await onSave({ ...form, titel: form.titel.trim(), text: form.text.trim() }, item?.id);
      onClose();
    } catch (err) {
      setError(err.message || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const label = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    color: 'var(--text-soft)', marginBottom: '0.4rem'
  };
  const input = {
    width: '100%', padding: '0.6rem 0.85rem',
    border: '1px solid var(--border)', borderRadius: 4,
    fontSize: '0.9rem', background: 'var(--white)', color: 'var(--text)',
    boxSizing: 'border-box'
  };

  return (
    <>
      {open && <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 200 }} />}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 480, maxWidth: '95vw',
        background: 'var(--white)', zIndex: 201, overflowY: 'auto',
        boxShadow: '-8px 0 40px rgba(0,0,0,.18)',
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform .28s cubic-bezier(.4,0,.2,1)',
        display: 'flex', flexDirection: 'column'
      }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', color: 'var(--forest)', margin: 0 }}>
            {item ? 'Redigera nyhet' : 'Nytt inlägg'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--text-soft)', padding: '0.2rem 0.4rem' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
          {error && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(176,48,48,.08)', border: '1px solid rgba(176,48,48,.3)', borderRadius: 4, fontSize: '0.88rem', color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          <div>
            <label style={label}>Rubrik</label>
            <input style={input} value={form.titel} onChange={e => set('titel', e.target.value)} placeholder="Rubrik på nyheten" />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
              <label style={{ ...label, marginBottom: 0 }}>Text</label>
              <button
                type="button"
                onClick={() => setPreview(p => !p)}
                style={{
                  fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.04em',
                  padding: '0.15rem 0.5rem', borderRadius: 3, cursor: 'pointer',
                  border: '1px solid var(--border)', background: preview ? 'var(--forest)' : 'var(--white)',
                  color: preview ? '#fff' : 'var(--text-soft)', transition: 'all .15s'
                }}
              >
                {preview ? 'Redigera' : 'Förhandsgranskning'}
              </button>
            </div>
            {preview ? (
              <div style={{
                ...input, minHeight: 140, padding: '0.75rem 0.85rem',
                overflowY: 'auto', fontSize: '0.88rem'
              }}>
                {form.text ? <Markdown content={form.text} /> : (
                  <span style={{ color: 'var(--text-soft)', fontStyle: 'italic' }}>Ingen text att förhandsgranska</span>
                )}
              </div>
            ) : (
              <textarea
                style={{ ...input, minHeight: 140, resize: 'vertical', lineHeight: 1.55 }}
                value={form.text}
                onChange={e => set('text', e.target.value)}
                placeholder="Nyhetens innehåll... (stödjer Markdown)"
              />
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <label style={label}>Datum</label>
              <input style={input} type="date" value={form.datum} onChange={e => set('datum', e.target.value)} />
            </div>
            <div>
              <label style={label}>Tagg</label>
              <input style={input} value={form.tagg} onChange={e => set('tagg', e.target.value)} placeholder="T.ex. Stämma" />
            </div>
          </div>

          <div>
            <label style={label}>Prioritet</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { v: false, label: 'Vanlig', desc: 'Sorteras efter datum' },
                { v: true,  label: '📌 Fäst', desc: 'Visas alltid högst upp' }
              ].map(({ v, label: l, desc }) => (
                <button key={String(v)} type="button" onClick={() => set('pinned', v)} style={{
                  flex: 1, padding: '0.6rem 0.5rem', borderRadius: 4, cursor: 'pointer', textAlign: 'left',
                  border: form.pinned === v ? '2px solid var(--forest)' : '1px solid var(--border)',
                  background: form.pinned === v ? 'rgba(44,74,46,.06)' : 'var(--white)',
                  transition: 'all .15s'
                }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-mid)' }}>{l}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)' }}>{desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 'auto', display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-ghost" style={{ flex: 1 }}>Avbryt</button>
            <button type="submit" disabled={saving} className="btn btn-forest" style={{ flex: 2 }}>
              {saving ? 'Sparar...' : item ? 'Spara ändringar' : 'Publicera'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── news row ────────────────────────────────────────────────────────── */

function NewsRow({ item, onEdit, onDelete, canEdit, canDelete }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.9rem',
        padding: '0.9rem 1rem', borderRadius: 5,
        border: '1px solid var(--border)',
        background: hov ? 'var(--cream)' : 'var(--white)',
        transition: 'background .15s'
      }}
    >
      {/* date column */}
      <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 60 }}>
        <div style={{ fontSize: '0.72rem', color: 'var(--text-soft)', lineHeight: 1.4 }}>{fmtDate(item.datum)}</div>
        {item.pinned && <div style={{ fontSize: '0.7rem', color: 'var(--forest)', fontWeight: 700, marginTop: '0.2rem' }}>📌</div>}
      </div>

      {/* content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text)' }}>{item.titel}</span>
          {item.tagg && <TagBadge tagg={item.tagg} />}
        </div>
        <p style={{
          fontSize: '0.8rem', color: 'var(--text-soft)', margin: 0,
          overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}>
          {item.text}
        </p>
      </div>

      {/* actions */}
      {canEdit && (
        <div style={{ display: 'flex', gap: '0.2rem', flexShrink: 0, opacity: hov ? 1 : 0.35, transition: 'opacity .15s' }}>
          <button onClick={() => onEdit(item)} title="Redigera" style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '0.35rem 0.5rem', borderRadius: 3, color: 'var(--text-soft)', fontSize: '0.85rem'
          }}>✏</button>
          {canDelete && (
            <button onClick={() => onDelete(item)} title="Ta bort" style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '0.35rem 0.5rem', borderRadius: 3, color: 'var(--danger)', fontSize: '0.85rem'
            }}>🗑</button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function NewsManager() {
  const { user, hasRole } = useAuth();
  const canEdit = hasRole('contentCreator');
  const canDelete = hasRole('contentCreator');
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  const notify = (message, type = 'success') => setToast({ message, type });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await ProtectedNewsAPI.getAll();
      const sorted = (d.news || []).sort((a, b) => {
        if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
        return new Date(b.datum) - new Date(a.datum);
      });
      setNews(sorted);
    } catch { notify('Kunde inte ladda nyheter', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditItem(null); setShowDrawer(true); };
  const openEdit = (item) => { setEditItem(item); setShowDrawer(true); };

  const handleSave = async (data, id) => {
    if (id) {
      await ProtectedNewsAPI.update(id, data);
    } else {
      await ProtectedNewsAPI.create(data);
    }
    await load();
    notify(id ? 'Nyhet uppdaterad' : 'Nyhet publicerad');
  };

  const handleDelete = async (item) => {
    if (!confirm(`Ta bort "${item.titel}"?`)) return;
    try {
      await ProtectedNewsAPI.delete(item.id);
      await load();
      notify('Nyhet borttagen');
    } catch { notify('Kunde inte ta bort', 'error'); }
  };

  const pinned = news.filter(n => n.pinned);
  const regular = news.filter(n => !n.pinned);

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '2rem 1.5rem' }}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', color: 'var(--forest)', margin: 0 }}>Nyheter</h2>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-soft)', marginTop: '0.2rem' }}>
            {news.length} inlägg · {pinned.length} fästa
          </p>
        </div>
        {canEdit && (
          <button onClick={openAdd} className="btn btn-forest" style={{ fontSize: '0.78rem' }}>
            + Nytt inlägg
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Laddar nyheter...</div>
      ) : news.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: 'var(--text-soft)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: 0.4 }}>📰</div>
          <div style={{ fontSize: '0.95rem', color: 'var(--text-mid)', marginBottom: '0.5rem' }}>Inga nyheter ännu</div>
          {canEdit && (
            <button onClick={openAdd} className="btn btn-forest" style={{ marginTop: '1rem', fontSize: '0.78rem' }}>
              + Skapa första nyheten
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {pinned.length > 0 && (
            <section>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-soft)', marginBottom: '0.6rem', paddingBottom: '0.4rem', borderBottom: '1px solid var(--border)' }}>
                Fästa <span style={{ fontWeight: 400, opacity: 0.6 }}>({pinned.length})</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {pinned.map(item => <NewsRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} canEdit={canEdit} canDelete={canDelete} />)}
              </div>
            </section>
          )}
          {regular.length > 0 && (
            <section>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-soft)', marginBottom: '0.6rem', paddingBottom: '0.4rem', borderBottom: '1px solid var(--border)' }}>
                Senaste <span style={{ fontWeight: 400, opacity: 0.6 }}>({regular.length})</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {regular.map(item => <NewsRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} canEdit={canEdit} canDelete={canDelete} />)}
              </div>
            </section>
          )}
        </div>
      )}

      <NewsDrawer open={showDrawer} onClose={() => setShowDrawer(false)} onSave={handleSave} item={editItem} />
    </div>
  );
}
